import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Stack, Typography } from '@mui/material'
import { compact, isNil, sortBy } from 'lodash'
import { toast } from 'react-toastify'
import useFetch from 'common/hooks/useFetch'
import useActions from 'common/hooks/useActions'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { NEW } from 'common/components/action/ActionConstant'
import { aquaFetch } from 'common/utils/ActionUtils'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import { normalizeString } from 'common/utils/StringUtil'
import ApplicationConf from 'conf/ApplicationConf'
import { defaultLotsFilter, getClientChiefId, IDEA_STATUSES, LOTS_STATUSES, matchPeriod, nextStatut, statusDef } from './LotsUtils'
import LotsFilterCard from './LotsFilterCard'
import LotsStatsCard from './LotsStatsCard'
import LotsTable from './LotsTable'
import LotCard from './LotCard'
import LotDialog from './LotDialog'
import NoteDialog from './NoteDialog'

// Vue réutilisable : liste (cartes/tableau) + dialogs + actions header.
// Bouton « Nouveau » : rattaché à newClientId s'il est défini, sinon client choisi parmi `customers`.
const LotsView = ({ lots: allLots = [], ideas = false, reload, allTickets = [], newClientId, emptyLabel = 'Aucun lot.', defaultView = 'cards', viewStorageKey = 'aquaparc-lots-view', customers = [], customer, filterStorageKey = 'aquaparc-lots-filter' }) => {
    const statuses = ideas ? IDEA_STATUSES : LOTS_STATUSES
    const { data: contacts = [] } = useFetch(ApplicationConf.gdl.contacts())
    const internalContacts = useMemo(() => contacts.filter(c => c.login), [contacts])
    const chiefItems = useMemo(() => sortBy(internalContacts.map(c => ({ id: c.idContact, label: c.name })), 'label'), [internalContacts])

    const lots = useMemo(() => allLots
        .filter(l => statuses.includes(statusDef(l.statut).value))
        .map(l => ({ ...l, chefName: internalContacts.find(c => c.idContact === l.idChefDeProjet)?.name })), [allLots, statuses, internalContacts])

    // Filtre chef de projet (menu Lots uniquement) : seuls les chefs présents sur au moins un lot.
    const lotChiefItems = useMemo(
        () => (ideas ? [] : chiefItems.filter(i => lots.some(l => l.idChefDeProjet === i.id))),
        [ideas, chiefItems, lots],
    )

    const getDefaultChief = useCallback(
        (idClient) => getClientChiefId(customers.find(c => c.id === idClient) ?? customer),
        [customers, customer],
    )
    const [editLot, setEditLot] = useState(undefined)
    const [lotDialogOpen, setLotDialogOpen] = useState(false)
    const [noteCtx, setNoteCtx] = useState(undefined) // { idLot, note, defaults }
    const [lotToDelete, setLotToDelete] = useState(undefined)
    const [view, setView] = useLocalStorage(viewStorageKey, defaultView)
    const [filter, setFilter] = useLocalStorage(filterStorageKey, defaultLotsFilter())

    const filteredLots = useMemo(() => {
        const { search = '', years = [], quarters = [], statuts = [], clients = [], chiefs = [] } = filter
        const bySearch = search
            ? lots.filter(l => normalizeString(compact([
                l.nom, l.commentaire, l.devis, l.bonCommande, l.clientName, l.chefName,
                ...(l.notes ?? []).map(n => n.commentaire),
            ]).join(' ')).includes(normalizeString(search)))
            : lots
        const byClient = clients.length ? bySearch.filter(l => clients.includes(l.idClient)) : bySearch
        const byChief = !ideas && chiefs.length ? byClient.filter(l => chiefs.includes(l.idChefDeProjet)) : byClient
        const byStatut = statuts.length ? byChief.filter(l => statuts.includes(l.statut)) : byChief
        return years.length || quarters.length ? byStatut.filter(l => matchPeriod(l, years, quarters)) : byStatut
    }, [lots, filter, ideas])

    const openNewLot = () => {
        setEditLot(undefined); setLotDialogOpen(true)
    }
    const handleEdit = useCallback((l) => {
        setEditLot(l); setLotDialogOpen(true)
    }, [])
    const handleAddNote = useCallback((l) => {
        const last = (l.notes ?? []).slice().sort((a, b) => a.date - b.date).pop()
        setNoteCtx({ idLot: l.id, note: undefined, defaults: last ? { statut: nextStatut(last.statut), avancement: last.avancement } : undefined })
    }, [])
    const handleEditNote = useCallback((l, n) => setNoteCtx({ idLot: l.id, note: n }), [])

    // Le lot en cours d'édition est relu dans la liste rechargée (non filtrée par menu) pour que le
    // tableau des notes de la popin reflète les ajouts / modifications de notes.
    const editLotFresh = allLots.find(l => l.id === editLot?.id) ?? editLot

    const canCreate = !isNil(newClientId) || customers.length > 0
    useActions(() => compact([
        canCreate && { type: NEW, onClick: openNewLot },
    ]), [canCreate])

    const onSaveLot = (lot) => {
        return aquaFetch(ApplicationConf.gdl.lot(), { method: isNil(lot.id) ? 'POST' : 'PUT', body: lot })
            .then(() => {
                setLotDialogOpen(false); setEditLot(undefined); reload()
            })
            .catch(() => toast.error('Erreur lors de l\'enregistrement du lot'))
    }
    const onDeleteLot = () => {
        aquaFetch(ApplicationConf.gdl.lotById(lotToDelete.id), { method: 'DELETE' })
            .then(() => {
                setLotToDelete(undefined); reload()
            })
            .catch(() => toast.error('Erreur lors de la suppression du lot'))
    }

    return (
        <>
            <Stack spacing={2}>
                <Grid container spacing={2}>
                    <Grid size={ideas ? 12 : 6}>
                        <LotsFilterCard lots={lots} customers={customers} chiefItems={lotChiefItems} statuses={statuses} filter={filter} onChange={setFilter} view={view} onChangeView={setView} />
                    </Grid>
                    {!ideas && (
                        <Grid size={6}>
                            <LotsStatsCard lots={filteredLots} />
                        </Grid>
                    )}
                </Grid>
                {lots.length === 0 && <Typography color='textSecondary'>{emptyLabel}</Typography>}
                {lots.length > 0 && filteredLots.length === 0 && <Typography color='textSecondary'>Aucun lot ne correspond aux filtres.</Typography>}
                {filteredLots.length > 0 && view === 'table' && (
                    <LotsTable
                        lots={filteredLots}
                        ideas={ideas}
                        onEdit={handleEdit}
                        onAddNote={handleAddNote}
                        onEditNote={handleEditNote}
                        onDelete={setLotToDelete}
                    />
                )}
                {filteredLots.length > 0 && view !== 'table' && filteredLots.map(lot => (
                    <LotCard
                        key={lot.id}
                        lot={lot}
                        ideas={ideas}
                        onEdit={handleEdit}
                        onAddNote={handleAddNote}
                        onEditNote={handleEditNote}
                        onDelete={setLotToDelete}
                    />
                ))}
            </Stack>
            {lotDialogOpen && (
                <LotDialog
                    lot={editLot}
                    notes={editLotFresh?.notes}
                    onAddNote={() => handleAddNote(editLotFresh)}
                    onEditNote={handleEditNote}
                    idClient={editLot?.idClient ?? newClientId}
                    defaultStatut={statuses[0]}
                    customers={customers}
                    chiefItems={chiefItems}
                    getDefaultChief={getDefaultChief}
                    allTickets={allTickets}
                    onClose={() => {
                        setLotDialogOpen(false); setEditLot(undefined)
                    }}
                    onSave={onSaveLot}
                />
            )}
            {!isNil(noteCtx) && (
                <NoteDialog
                    note={noteCtx.note}
                    idLot={noteCtx.idLot}
                    defaults={noteCtx.defaults}
                    onClose={() => setNoteCtx(undefined)}
                    onSaved={reload}
                />
            )}
            <ConfirmModal
                isOpen={!isNil(lotToDelete)}
                title={`Supprimer le lot « ${lotToDelete?.nom ?? ''} » ?`}
                onValidate={onDeleteLot}
                onClose={() => setLotToDelete(undefined)}
            />
        </>
    )
}
LotsView.propTypes = {
    lots: PropTypes.arrayOf(PropTypes.shape({})),
    ideas: PropTypes.bool,
    reload: PropTypes.func,
    allTickets: PropTypes.arrayOf(PropTypes.shape({})),
    newClientId: PropTypes.number,
    emptyLabel: PropTypes.string,
    defaultView: PropTypes.oneOf(['cards', 'table']),
    viewStorageKey: PropTypes.string,
    customers: PropTypes.arrayOf(PropTypes.shape({})),
    customer: PropTypes.shape({}),
    filterStorageKey: PropTypes.string,
}

export default LotsView
